/**
 * What the shell knows without opening a page - as one read, and as a stream.
 *
 * This used to be nine parallel requests assembled in the browser once a minute.
 * Two things were wrong with that, and only one of them was the request count.
 * Nine reads taken a poll apart can disagree - the rail claiming seven unread
 * while the mail menu lists three - and a badge that contradicts the page it
 * opens teaches people to stop reading badges. And a minute is a long time to
 * be wrong about a phone that is ringing now.
 *
 * So the aggregate moved to the server (`crm-dashboard/shell/collect.ts`), which
 * knows the moment a Twilio webhook lands or a ticket is filed, and can simply
 * say so. `getShellSignals` is the same payload as one request, for the first
 * paint and for the fallback when a stream cannot be had.
 *
 * The stream is SSE rather than a WebSocket because it is an ordinary GET under
 * `/api` - the one path the CRM's nginx block already proxies - and because the
 * browser handles reconnection itself. Nothing is ever sent UP it; when this
 * client wants something to change it posts, like always, and the resulting
 * push arrives on its own.
 */
import { API_BASE, api } from "./api";
import type { BgAlert, BgBeat, BgJob } from "./background";

/**
 * The counters the payload can carry.
 *
 * The server's own list is `BadgeKey` in `crm-dashboard/shell/types.ts`, and
 * `crm/nav.ts` aliases this one rather than keeping a third copy - which is why
 * a mistyped badge key is a compile error here instead of a badge that silently
 * never draws.
 */
export type BadgeKey =
  | "mailUnread"
  | "leads"
  | "salesOverdue"
  | "tasksOpen"
  | "ticketsOpen"
  | "callsMissed"
  | "brainDue"
  /** Confirmed, still-open outages of watched customer sites (Saidiseire). */
  | "sitesDown";

export type BadgeCounts = Partial<Record<BadgeKey, number>>;

/** Something that happened and is worth a line in the notification menu. */
export interface SignalRow {
  key: string;
  kind:
    | "overdue"
    | "campaign"
    | "leads"
    | "tickets"
    | "callsMissed"
    | "voicemail"
    /** Runs this operator started. */
    | "brainDoneMine"
    /** Runs anybody else started, the autopilot included. */
    | "brainDone"
    /** Watched customer sites with a confirmed outage still open. A
     *  condition, not an event: it clears itself when the sites come back.
     *  Its key is `sitesDown:<newest incident id>`, so an outage that starts
     *  after the menu was cleared at the same count still shows. */
    | "sitesDown";
  /** Already-formatted count or name the menu shows after the label. */
  detail: string;
  count: number;
  /**
   * True when this count is ALREADY per-operator, filtered by a cursor the
   * server holds.
   *
   * The browser-local clear store must not filter such a row a second time. That
   * store compares counts, and a count the server has just reset to zero sits
   * below whatever number was last written there - so the row would stay hidden
   * for good, including when a new call arrives.
   */
  tracked?: boolean;
}

export interface ShellSignals {
  badges: BadgeCounts;
  /** Unattended runs in flight. */
  jobs: BgJob[];
  /** Background work that ended badly and nobody has looked at. */
  attention: BgAlert[];
  /** Each engine's pulse, so an idle system can prove it is alive. */
  beats: BgBeat[];
  signals: SignalRow[];
  /** Overdue money, in cents - the notification menu wants the amount, not only
   *  the row count. */
  overdueCents: number;
}

export const EMPTY_SIGNALS: ShellSignals = {
  badges: {},
  jobs: [],
  attention: [],
  beats: [],
  signals: [],
  overdueCents: 0,
};

export const getShellSignals = (): Promise<ShellSignals> => api.get<ShellSignals>("/admin/crm/shell");

export interface ShellStream {
  close(): void;
}

export interface ShellStreamHandlers {
  onData(next: ShellSignals): void;
  /**
   * The connection went away. `permanent` distinguishes the two cases the
   * caller has to treat differently: the browser retries a dropped connection
   * by itself, but a refused one (401 after the session expired, 503 when the
   * server is holding too many streams) is closed for good and needs a
   * fallback.
   */
  onError(permanent: boolean): void;
}

/**
 * Open the live stream. Returns a handle whose `close` is safe to call twice.
 *
 * The session cookie rides along because the URL is same-origin; `EventSource`
 * cannot set headers, which is exactly why this endpoint authenticates from the
 * cookie like every other one rather than from a bearer token.
 */
export function openShellSignalsStream(handlers: ShellStreamHandlers): ShellStream {
  const source = new EventSource(`${API_BASE}/admin/crm/shell/stream`);
  let closed = false;

  source.addEventListener("signals", (event) => {
    if (closed) return;
    try {
      handlers.onData(JSON.parse((event as MessageEvent<string>).data) as ShellSignals);
    } catch {
      // A payload this client cannot parse is a payload it cannot act on. The
      // stream stays open: the next push replaces it wholesale anyway, so one
      // bad frame is a moment without an update rather than a dead shell.
    }
  });

  source.onerror = () => {
    if (closed) return;
    handlers.onError(source.readyState === EventSource.CLOSED);
  };

  return {
    close() {
      if (closed) return;
      closed = true;
      source.close();
    },
  };
}
